<template>
  <v-container>
    <div class="elevation-2 px-5 mt-3">
      <SimplePageHeader title="Workflow Dashboard" class="px-4 pr-5 mb-5" />

      <!-- Summary Cards -->
      <v-row class="px-4 mb-4">
        <v-col cols="12" sm="6" md="3">
          <v-card outlined>
            <v-card-text class="text-center">
              <div class="display-1 font-weight-bold primary--text">{{ summary.totalActiveWorkflows }}</div>
              <div class="subtitle-2 mt-1">Active Workflows</div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-card outlined :color="summary.overdueTasksCount > 0 ? 'red lighten-5' : ''">
            <v-card-text class="text-center">
              <div
                class="display-1 font-weight-bold"
                :class="summary.overdueTasksCount > 0 ? 'red--text text--darken-1' : 'primary--text'"
              >{{ summary.overdueTasksCount }}</div>
              <div class="subtitle-2 mt-1">Overdue Tasks</div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-card outlined :color="summary.tasksDueTodayCount > 0 ? 'orange lighten-5' : ''">
            <v-card-text class="text-center">
              <div
                class="display-1 font-weight-bold"
                :class="summary.tasksDueTodayCount > 0 ? 'orange--text text--darken-2' : 'primary--text'"
              >{{ summary.tasksDueTodayCount }}</div>
              <div class="subtitle-2 mt-1">Due Today</div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-card outlined>
            <v-card-text class="text-center">
              <div class="display-1 font-weight-bold primary--text">{{ summary.tasksDueThisWeekCount }}</div>
              <div class="subtitle-2 mt-1">Due This Week</div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Filters -->
      <v-row class="px-5 mb-sm-5">
        <v-col cols="12" sm="3">
          <v-select
            v-model="statusId"
            :items="instanceStatusOptions"
            item-text="name"
            item-value="id"
            label="All Statuses"
            clearable
            outlined
          />
        </v-col>
        <v-col cols="12" sm="3">
          <BusinessEntitiesDropDownList
            v-model="businessEntityId"
            :clearable="true"
            @businessEntityIdChange="handleBusinessEntityChange"
          />
        </v-col>
        <v-col cols="12" sm="3">
          <ClientsDropDownList
            :id="clientId"
            :businessEntityId="businessEntityId"
            :isRequired="false"
            :clearable="true"
            @clientIdChange="clientId = $event"
          />
        </v-col>
        <v-col cols="12" sm="3">
          <UsersDropDownList
            :userId="assignedToUserId"
            :isRequired="false"
            :clearable="true"
            label="Assignee"
            @userIdChange="assignedToUserId = $event"
          />
        </v-col>
        <v-col cols="12" sm="3">
          <v-text-field
            v-model="targetStartDateFrom"
            label="Start Date From"
            type="date"
            outlined
            clearable
          />
        </v-col>
        <v-col cols="12" sm="3">
          <v-text-field
            v-model="targetStartDateTo"
            label="Start Date To"
            type="date"
            outlined
            clearable
          />
        </v-col>
      </v-row>
      <v-row justify="end">
        <v-col cols="12" md="6">
          <FilterButtons
            @search="handleSearchClick"
            @clear="handleClearClick"
          ></FilterButtons>
        </v-col>
      </v-row>
      <FilterButtonsSmall
        @search="handleSearchClick"
        @clear="handleClearClick"
      ></FilterButtonsSmall>
    </div>

    <!-- Instance Table -->
    <v-container fluid class="px-0 pt-2 mt-4">
      <v-layout column>
        <v-data-table
          dense
          :headers="headers"
          :items="workflowInstanceList"
          :options.sync="options"
          :server-items-length="serverItemsLength"
          item-key="id"
          class="elevation-2"
        >
          <template v-slot:item.candidateClient="{ item }">
            <span>{{ item.candidateName }}<br><span class="grey--text caption">{{ item.clientName }}</span></span>
          </template>
          <template v-slot:item.targetStartDate="{ item }">
            {{ item.targetStartDate | formatDate }}
          </template>
          <template v-slot:item.progress="{ item }">
            <div class="d-flex align-center" style="min-width: 110px">
              <v-progress-linear
                :value="item.progressPercentage"
                height="8"
                rounded
                class="mr-2"
                color="primary"
              />
              <span class="caption grey--text text--darken-1">{{ item.progressPercentage }}%</span>
            </div>
          </template>
          <template v-slot:item.statusName="{ item }">
            <v-chip
              x-small
              label
              :color="instanceStatusAttrs(item.statusId).color"
              :dark="instanceStatusAttrs(item.statusId).dark"
            >{{ item.statusName }}</v-chip>
          </template>
          <template v-slot:item.action="{ item }">
            <v-btn
              class="mx-1 my-1"
              color="primary"
              outlined
              @click.prevent="viewInstance($event, item)"
            >
              <v-icon color="primary">visibility</v-icon>
            </v-btn>
            <v-btn
              v-if="item.statusId !== workflowInstanceStatus.Cancelled && item.statusId !== workflowInstanceStatus.Complete"
              class="mx-1 my-1"
              color="error"
              outlined
              @click="openCancelDialog(item)"
            >
              <v-icon>cancel</v-icon>
            </v-btn>
          </template>
        </v-data-table>
      </v-layout>
    </v-container>

    <ConfirmationModal
      :title="'Cancel Workflow Confirmation'"
      :contentText="cancelConfirmationText"
      :toolbarColor="'primary'"
      :showModal="showCancelConfirmationDialog"
      :loading="confirmingCancel"
      @cancel-click="cancelCancelOperation"
      @ok-click="confirmCancel"
    />

    <BottomSpacer />
  </v-container>
</template>

<script>
import { mapActions } from 'vuex'
import { mapFields } from '@/vueHelpers/vuex'
import { BottomSpacer, FilterButtons, FilterButtonsSmall } from '@/components/buttons'
import { SimplePageHeader } from '@/components/pageComponents'
import { ConfirmationModal } from '@/components/dialogs'
import { BusinessEntitiesDropDownList } from '@/components/businessEntities'
import { ClientsDropDownList } from '@/components/clients'
import { UsersDropDownList } from '@/components/users'
import { workflowInstanceDetailRoute } from '@/lib/constants/routes'
import workflowStatusMixin from './workflowStatusMixin'
import { WORKFLOW_INSTANCE_STATUS_OPTIONS, workflowInstanceStatus } from '@/lib/constants/workflowStatuses'
import { parseGridOptionsFromQuery, buildPaginationQueryParams, isAllRecordsSortOnlyChange, setOrDeleteQueryParam, hasQueryChanged } from '@/lib/pagingAndSorting'

export default {
  mixins: [workflowStatusMixin],
  components: {
    BottomSpacer,
    SimplePageHeader,
    ConfirmationModal,
    BusinessEntitiesDropDownList,
    ClientsDropDownList,
    UsersDropDownList,
    FilterButtons,
    FilterButtonsSmall
  },
  data () {
    return {
      workflowInstanceStatus,
      instanceStatusOptions: WORKFLOW_INSTANCE_STATUS_OPTIONS,
      showCancelConfirmationDialog: false,
      cancelTarget: null,
      confirmingCancel: false,
      hasFetchedAllInstances: false,
      options: {
        page: 1,
        itemsPerPage: 10,
        sortBy: [],
        sortDesc: []
      },
      headers: [
        { text: 'Candidate / Client', value: 'candidateClient', sortable: false, width: '28%' },
        { text: 'Target Start', value: 'targetStartDate', align: 'center', sortable: false, width: '12%' },
        { text: 'Progress', value: 'progress', sortable: false, width: '18%' },
        { text: 'Status', value: 'statusName', align: 'center', sortable: false, width: '12%' },
        { text: 'Actions', value: 'action', align: 'center', sortable: false, width: '10%' }
      ]
    }
  },
  computed: {
    ...mapFields('workflowInstances', ['workflowInstanceList', 'totalRecords']),
    ...mapFields('workflowDashboard', ['summary']),
    ...mapFields('workflowDashboardFilters', [
      'statusId',
      'businessEntityId',
      'clientId',
      'assignedToUserId',
      'targetStartDateFrom',
      'targetStartDateTo'
    ]),
    serverItemsLength () {
      return this.options.itemsPerPage === -1 ? undefined : this.totalRecords
    },
    cancelConfirmationText () {
      if (!this.cancelTarget) return ''
      return `Are you sure you want to cancel the workflow '${this.cancelTarget.templateName}' for '${this.cancelTarget.candidateName}'? This cannot be undone.`
    }
  },
  watch: {
    options: {
      handler (newOptions, previousOptions) {
        this.syncOptionsToQuery()

        if (!this.shouldFetchInstances(newOptions, previousOptions)) return

        this.fetchInstances()
      },
      deep: true
    }
  },
  methods: {
    ...mapActions('workflowInstances', ['getWorkflowInstancesWithFiltersPaginated', 'getWorkflowInstancesWithDashboardFilters']),
    ...mapActions('workflowDashboard', ['getWorkflowDashboardSummary']),
    ...mapActions('workflowDashboardFilters', ['clearFilters']),
    ...mapActions('workflowInstance', ['updateInstanceStatus']),
    viewInstance (event, instance) {
      if (event.ctrlKey === true) {
        const route = this.$router.resolve({ name: workflowInstanceDetailRoute.name, params: { id: instance.id } })
        window.open(route.href, '_blank')
      } else {
        this.$router.push({ name: workflowInstanceDetailRoute.name, params: { id: instance.id } })
      }
    },
    openCancelDialog (instance) {
      this.cancelTarget = instance
      this.showCancelConfirmationDialog = true
    },
    cancelCancelOperation () {
      this.showCancelConfirmationDialog = false
      this.cancelTarget = null
    },
    async confirmCancel () {
      if (!this.cancelTarget) return
      this.confirmingCancel = true
      try {
        await this.updateInstanceStatus({ id: this.cancelTarget.id, statusId: workflowInstanceStatus.CANCELLED })
        this.refresh()
      } catch (e) {
        // error already notified by store action
      }
      this.confirmingCancel = false
      this.showCancelConfirmationDialog = false
      this.cancelTarget = null
    },
    handleSearchClick () {
      this.options = { ...this.options, page: 1 }
      this.syncOptionsToQuery()
    },
    handleClearClick () {
      this.clearFilters()
      this.handleSearchClick()
    },
    handleBusinessEntityChange () {
      this.clientId = null
    },
    getOptionsFromQuery () {
      return parseGridOptionsFromQuery(this.$route.query, this.options)
    },
    applyFiltersFromQuery () {
      const { statusId, businessEntityId, clientId, assignedToUserId, targetStartDateFrom, targetStartDateTo } = this.$route.query
      const parseId = value => {
        const parsedValue = Number(value)
        return Number.isInteger(parsedValue) && parsedValue > 0 ? parsedValue : null
      }

      this.statusId = parseId(statusId)
      this.businessEntityId = parseId(businessEntityId)
      this.clientId = parseId(clientId)
      this.assignedToUserId = parseId(assignedToUserId)
      this.targetStartDateFrom = typeof targetStartDateFrom === 'string' && targetStartDateFrom.length ? targetStartDateFrom : null
      this.targetStartDateTo = typeof targetStartDateTo === 'string' && targetStartDateTo.length ? targetStartDateTo : null
    },
    syncOptionsToQuery () {
      const { sortColumn, sortDirection, ...paginationParams } = buildPaginationQueryParams(this.options)
      const nextQuery = { ...this.$route.query, ...paginationParams }

      setOrDeleteQueryParam(nextQuery, 'sortColumn', sortColumn)
      setOrDeleteQueryParam(nextQuery, 'sortDirection', sortColumn ? sortDirection : null)
      setOrDeleteQueryParam(nextQuery, 'statusId', this.statusId)
      setOrDeleteQueryParam(nextQuery, 'businessEntityId', this.businessEntityId)
      setOrDeleteQueryParam(nextQuery, 'clientId', this.clientId)
      setOrDeleteQueryParam(nextQuery, 'assignedToUserId', this.assignedToUserId)
      setOrDeleteQueryParam(nextQuery, 'targetStartDateFrom', this.targetStartDateFrom)
      setOrDeleteQueryParam(nextQuery, 'targetStartDateTo', this.targetStartDateTo)

      if (hasQueryChanged(this.$route.query, nextQuery, [
        'pageNumber', 'pageSize', 'sortColumn', 'sortDirection', 'statusId', 'businessEntityId', 'clientId',
        'assignedToUserId', 'targetStartDateFrom', 'targetStartDateTo'
      ])) {
        this.$router.replace({ query: nextQuery })
      }
    },
    fetchInstances () {
      const { page, itemsPerPage, sortBy, sortDesc } = this.options

      if (itemsPerPage === -1) {
        this.getWorkflowInstancesWithDashboardFilters().then(instances => {
          this.hasFetchedAllInstances = true
          this.workflowInstanceList = instances
        })
        return
      }

      this.hasFetchedAllInstances = false
      this.getWorkflowInstancesWithFiltersPaginated({
        pageNumber: page,
        pageSize: itemsPerPage,
        sortColumn: sortBy && sortBy.length ? sortBy[0] : null,
        sortDirection: sortDesc && sortDesc.length ? (sortDesc[0] ? 'DESC' : 'ASC') : null
      }).then(instances => {
        this.workflowInstanceList = instances
      })
    },
    shouldFetchInstances (newOptions, previousOptions) {
      return !isAllRecordsSortOnlyChange(newOptions, previousOptions, this.hasFetchedAllInstances)
    },
    refresh () {
      this.fetchInstances()
      this.getWorkflowDashboardSummary()
    }
  },
  created () {
    this.applyFiltersFromQuery()
    this.options = {
      ...this.options,
      ...this.getOptionsFromQuery()
    }
    this.syncOptionsToQuery()
    this.getWorkflowDashboardSummary()
  }
}
</script>
